// Shared membership helpers (frontend).
//
// The canonical "is this user currently entitled to paid features?" rule lives
// on the backend in BillingManager.isCurrentlyEntitled (revolt-site/backend/
// src/managers/billing-manager.js). This file mirrors that logic for UI
// gating so the frontend never disagrees with the server about whether a
// past_due-but-in-grace user can still see paid surfaces.
//
// Rules (must stay in sync with the backend):
//   - planId missing or "free" → not entitled.
//   - status active / trialing / "" → entitled iff accessUntil is missing or
//     in the future.
//   - status past_due → entitled iff graceUntil exists and is in the future.
//     This is the 4-day grace window after a failed renewal payment.
//   - any other status (canceled, cancelled, unpaid as final state, free) →
//     not entitled.
//
// Note: Stripe's literal `unpaid` subscription status maps to past_due on the
// Revolt membership model (see applySubscriptionStateToUser). Frontend code
// should NEVER look at `subscription.status === "unpaid"` to decide
// entitlement; only look at `membership.status` and `membership.graceUntil`.

/**
 * @param {Object|null|undefined} membership
 * @returns {boolean}
 */
export function isEntitled(membership) {
  if (!membership || typeof membership !== 'object') {
    return false;
  }

  const planId =
    typeof membership.planId === 'string'
      ? membership.planId.trim().toLowerCase()
      : '';
  if (!planId || planId === 'free') {
    return false;
  }

  const status =
    typeof membership.status === 'string'
      ? membership.status.trim().toLowerCase()
      : '';
  const now = new Date();

  if (status === 'active' || status === 'trialing' || status === '') {
    const accessUntil = membership.accessUntil ? new Date(membership.accessUntil) : null;
    if (!accessUntil || Number.isNaN(accessUntil.getTime())) {
      return true;
    }
    return accessUntil > now;
  }

  if (status === 'past_due') {
    const graceUntil = membership.graceUntil ? new Date(membership.graceUntil) : null;
    if (!graceUntil || Number.isNaN(graceUntil.getTime())) {
      return false;
    }
    return graceUntil > now;
  }

  return false;
}

/**
 * True only when the user is in an active 4-day grace window after a failed
 * renewal payment. UI affordances (banner, retry-payment CTA) should hinge on
 * this rather than on raw `membership.status` so we never surface them to a
 * user whose graceUntil has already elapsed.
 *
 * @param {Object|null|undefined} membership
 * @returns {boolean}
 */
export function isInGrace(membership) {
  if (!membership || typeof membership !== 'object') {
    return false;
  }
  const status =
    typeof membership.status === 'string'
      ? membership.status.trim().toLowerCase()
      : '';
  if (status !== 'past_due') {
    return false;
  }
  const graceUntil = membership.graceUntil ? new Date(membership.graceUntil) : null;
  if (!graceUntil || Number.isNaN(graceUntil.getTime())) {
    return false;
  }
  return graceUntil > new Date();
}

/**
 * Whole-day countdown to graceUntil. Returns 0 once the deadline has passed.
 * Caller decides how to format ("X day(s) left" vs "less than 1 day").
 *
 * @param {Object|null|undefined} membership
 * @returns {{ msRemaining: number, daysRemaining: number, hoursRemaining: number, graceUntil: Date|null }}
 */
export function getGraceCountdown(membership) {
  const fallback = { msRemaining: 0, daysRemaining: 0, hoursRemaining: 0, graceUntil: null };
  if (!membership || typeof membership !== 'object') return fallback;
  const graceUntil = membership.graceUntil ? new Date(membership.graceUntil) : null;
  if (!graceUntil || Number.isNaN(graceUntil.getTime())) return fallback;
  const msRemaining = Math.max(0, graceUntil.getTime() - Date.now());
  const hoursRemaining = Math.floor(msRemaining / (60 * 60 * 1000));
  const daysRemaining = Math.floor(msRemaining / (24 * 60 * 60 * 1000));
  return { msRemaining, daysRemaining, hoursRemaining, graceUntil };
}
